import React, { useEffect, useState } from "react";
import "./Invoice.css";
import useSwal from "../../../../common/ReusableComponent/useSwal";
import { DatePicker } from "antd";
import { useToastHandler } from "../../../../common/ToastHandler";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { FilterButton } from "../../../../../utils/api-manager/Forms/SvgIcons";
import LoadingOverlay from "../../../../common/LoadingBar.js";
import loader from "../../../../../assets/Image/Loader2.gif";
import useFormattingUtils from "../../../../../context/useFormattingUtils";

const InvoiceTable = ({ reloadData }) => {
  const { Get, Post, _Get , GetPaginatedData} = useApi();
  const {handleToast} = useToastHandler()
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [sortOrder, setSortOrder] = useState({ column: "", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10;
  const [selectedVendor, setSelectedVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const { confirmDelete } = useSwal();
  const [reloaData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(false);
  const {formatDate} = useFormattingUtils();
  const [totalPages, setTotalPages] = useState(1);
  const fetchInvoices = async ({
    page = 1,
    searchTerm = "",
    status = "",
    vendor = "",
    dateRange = []
  } = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        ...(status && status !== "All" ? { status } : {}),
        ...(vendor ? { vendor } : {}),
        ...(dateRange.length === 2
          ? { start_date: dateRange[0], end_date: dateRange[1] }
          : {}),
      };
      const response = await GetPaginatedData("invoiceData", {
        page,
        search: searchTerm,
        queryParams,
      });
      const results = response?.data || [];
      const count = response?.totalCount || 0;
      const totalPages = Math.ceil(count / 10);
      setTotalPages(totalPages);
      setInvoices(results.filter((invoice) => !invoice.is_deleted));

      const uniqueVendors = [
        ...new Set(
          results.map((invoice) => invoice.vendor_department_name)
        ),
      ];
      setVendors(uniqueVendors);

      return { data: results, totalPages };
    } catch (error) {
      console.error("Error fetching invoices:", error);

      const backendErrors = error?.response?.data;

      let errorMessage = "Failed to fetch invoice data.";

      if (backendErrors && typeof backendErrors === "object") {
        const firstKey = Object.keys(backendErrors)[0];
        const messages = backendErrors[firstKey];
        if (Array.isArray(messages) && messages.length > 0) {
          errorMessage = messages[0]; 
        }
      }

      handleToast({
        type: "ERROR",
        message: errorMessage,
      });

      setInvoices([]);
      setVendors([]);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 600);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchInvoices({
      page: currentPage,
      searchTerm: debouncedSearchQuery,
      status: selectedStatus,
      vendor: selectedVendor,
      dateRange,
    });
  }, [currentPage, debouncedSearchQuery, selectedStatus, selectedVendor, dateRange, reloadData]);

  useEffect(() => {
    fetchInvoices({ page: currentPage, searchTerm: debouncedSearchQuery });
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchInvoices({ page: 1, searchTerm: debouncedSearchQuery });
    }
  }, [dateRange, selectedStatus, selectedVendor, debouncedSearchQuery]);

  const handleSort = (column) => {
    const direction =
      sortOrder.column === column && sortOrder.direction === "asc"
        ? "desc"
        : "asc";
    setSortOrder({ column, direction });

    const sortedInvoices = [...invoices].sort((a, b) => {
      let aValue = a[column];
      let bValue = b[column];

      if (column === 'amount' || column === 'remaining_amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (column === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setInvoices(sortedInvoices);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;

    try {
      await Post(`deleteInvoice`, { invoice_id: id });

      handleToast({
        type:"SUCCESS" ,
        message:"Invoice successfully deleted!"
      })
    } catch (error) {
      if (error.response) {
        handleToast({
          type:"ERROR" ,
          message:"Error deleting invoice: " + error.response.data
        })
      } else if (error.request) {
        handleToast({
          type:"ERROR" ,
          message:"No response received: " + error.request
        })
      } else {
        handleToast({
          type:"ERROR" ,
          message:"Error setting up request: " + error.message
        })
      }
    }
  };

  const getSortIcon = (column) => {
    if (sortOrder.column === column) {
      return (
        <svg
          width="8"
          height="10"
          viewBox="0 0 8 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 6L4 10L8 6H0Z" fill="black" />
          <path d="M0 4L4 0L8 4H0Z" fill="black" />
        </svg>
      );
    }
    return null;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxPages = 5;
    let start = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let end = Math.min(totalPages, start + maxPages - 1);
    if (end - start < maxPages) {
      start = Math.max(1, end - maxPages + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={currentPage === i ? "active-pgbtn" : "pgbtn"}
          onClick={() => handlePageClick(i)}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="pagination">

        {pages}

      </div>
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedStatus("All");
    setSelectedVendor("");
    setDateRange([]);
    setCurrentPage(1);
  };

  if (loading && searchQuery === debouncedSearchQuery) {
    return (
      <LoadingOverlay loading={loading} loader={loader} />
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: "10px" }}>

    <div className="expense-searchcontainerstart d-flex">
          <div className="search-container">
            <input
              type="text"
              style={{
                width: "300px",
                height: "45px",
                backgroundColor: "#fff",
                border: "1px solid #DBDBDB",
                boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)",
              }}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); 
              }}
              placeholder="Search"
            />
            <svg
              className="search-container-icon"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5416 19.2497C15.3511 19.2497 19.2499 15.3508 19.2499 10.5413C19.2499 5.73186 15.3511 1.83301 10.5416 1.83301C5.73211 1.83301 1.83325 5.73186 1.83325 10.5413C1.83325 15.3508 5.73211 19.2497 10.5416 19.2497Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.1666 20.1663L18.3333 18.333"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

        </div>
        <div className="d-flex align-items-center">
          <div
            className="filterbutton"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "60px",
              padding: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <button
              className="btn dropdown"
              type="button"
              id="dropdownMenuButton"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                fontSize: "14px",
                marginLeft: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
                border: "none",
                background: "transparent",
                outline: "none",
              }}
            >
              <FilterButton />
              &nbsp;
            </button>

            <ul
              className="dropdown-menu p-3"
              aria-labelledby="dropdownMenuButton"
              style={{
                width: "290px",
                justifyContent: "center",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
              }}
            >
              <li>
                <div className="mb-2">
                  <select
                    id="selectStatus"
                    className="form-select"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{ borderRadius: "60px", padding: "auto" }}
                  >
                    <option value="All">All Status</option>
                    {["unpaid", "partially_paid", "paid",].map((status) => (
                      <option key={status} value={status}>
                        {status === "partially_paid"
                          ? "Partially Paid"
                          : status &&
                            status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
              <li>
                <div className="mb-2">
                  <select
                    id="selectVendor"
                    className="form-select"
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    style={{ borderRadius: "60px", padding: "auto" }}
                  >
                    <option value="">All Vendors</option>
                    {vendors.map((vendor) => (
                      <option key={vendor} value={vendor}>
                        {vendor}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
              <li className="text-center mt-2">
                <button
                  className="btn btn-secondary me-2"
                  style={{ width: "75px", height: "40px" }}
                  onClick={clearFilters}
                >
                  Clear
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div>
          <DatePicker.RangePicker
            style={{ borderRadius: "60px", height: "40px", width: "300px" }}
            onChange={(dates) => setDateRange(dates || [])} 
            format="YYYY-MM-DD"
            value={dateRange}
            placeholder={['Start Date', 'End Date']}
          />
        </div>
      </div>

      <table
        className="data-table custom-table"
        style={{
          borderRadius: "20px 20px 0 0",
          border: "1px solid #EBEAF2",
          borderCollapse: "separate",
          borderSpacing: 0,
          overflow: "hidden",
        }}
      >
        <thead className="table-header">
          <tr>
            {[
              "date",
              "vendor Name",
              "amount",
              "remaining_amount",
              "payment_method",
              "status",
              "actions",
            ].map((header, index) => (
              <th key={header} onClick={() => header !== 'actions' && handleSort(header)} style={{ cursor: header !== 'actions' ? 'pointer' : 'default' }}>
                {header
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase())}{" "}
                {header !== 'actions' && getSortIcon(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          {invoices.length > 0 ? (
            invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td>{formatDate(invoice.date)}</td>
                <td>{invoice.vendor_department_name}</td>
                <td>{invoice.amount}</td>
                <td>{invoice.remaining_amount}</td>
                <td>
                  <span style={getPaymentMethodStyle(invoice.pay_method)}>
                    {invoice.pay_method
                      .replace(/_/g, " ")
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </span>
                </td>
                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                  <span style={getStatusStyle(invoice.status)}>
                    {invoice.status
                      .replace(/_/g, " ")
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </span>
                </td>
                <td>
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-vertical"
                      type="button"
                      id={`actionDropdown-${invoice.id}`}
                      data-bs-toggle="dropdown"
                    >
                      <i
                        className="bi bi-three-dots"
                        style={{ transform: "rotate(90deg)", display: "block" }}
                      ></i>
                    </button>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby={`actionDropdown-${invoice.id}`}
                    >
                      <li>
                        <a
                          className="dropdown-Delete"
                          onClick={() => handleDelete(invoice.id)}
                        >
                          Delete
                        </a>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan="7"
                style={{
                  textAlign: "center",
                  
                  fontWeight: "600",
                  fontSize: "24px",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.1) -92.86%, #4A5546 71.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                No data found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {renderPagination()}
    </div>
  );
};

const paymentMethodStyles = {
  bank: { backgroundColor: "" },
  "Business Cash": { backgroundColor: "" },
  "Credit Card": { backgroundColor: "" },
  cash: { backgroundColor: "" },
  cheque: { backgroundColor: "" },
  pay_later: { backgroundColor: "" },
  pay_now: { backgroundColor: "" },
  credit_invoice: { backgroundColor: "" },
};

const getPaymentMethodStyle = (method) => ({
  ...(paymentMethodStyles[method] || { backgroundColor: "#6c757d" }),
  borderRadius: "5px",
  padding: "10px 20px",
  color: "#000",
});

const statusStyles = {
  draft: {
    backgroundColor: "#D1D1EF",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  unpaid: {
    backgroundColor: "#ff2024",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  paid: {
    backgroundColor: "#198f51",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  partially_paid: {
    backgroundColor: "#0c8ce9",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const getStatusStyle = (status) => ({
  ...(statusStyles[status] || { backgroundColor: "#198f51" }),
  display: "inline-flex", 
  justifyContent: "center", 
  alignItems: "center",
  width: "130px", 
  maxWidth:"100%",
  height: "30px", 
  borderRadius: "5px",
  padding: "5px 10px",
  color: "#fff",
  fontSize: "14px",
  textAlign: "center",
});

const getCountClass = (status) => ({
  ...statusStyles[status],
  padding: "10px",
});

export default InvoiceTable;